import { Button } from "@freightsimple/ui-core/Controls/Button";
import { ControlPresentationProvider } from "@freightsimple/ui-core/Controls/ControlPresentationProvider";
import { Dialog } from "@freightsimple/ui-core/Dialog/Dialog";
import { Heading } from "@freightsimple/ui-core/Typography/Heading";
import { Stack } from "@freightsimple/ui-core/ComponentLayout/Stack";
import { T } from "@freightsimple/ui-core/Typography/T";
import type { UUID } from "@/Shared/types";
import { BookShipmentNoQuotes } from "./CustomComponents/BookShipmentNoQuotes";
import { BookShipmentQuotingDisabled } from "./CustomComponents/BookShipmentQuotingDisabled";
import { ShipmentLoadingError } from "@/Shared/Shipment/CustomComponents/ShipmentLoadingError";
import { useShipmentService } from "@/Services/ShipmentService";
import type { EquipmentType } from "@freightsimple/generated-dashboard-openapi-client/models/EquipmentType";
import type { PreBookingShipment } from "@freightsimple/generated-dashboard-openapi-client/models/PreBookingShipment";
import type { SavedLocation } from "@freightsimple/generated-dashboard-openapi-client/models/SavedLocation";
import type { SharedDedicated } from "@freightsimple/generated-dashboard-openapi-client/models/SharedDedicated";
import moment from "moment";
import { useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";
import { BookShipmentWarningMessages } from "./CustomComponents/BookShipmentWarningMessages";
import { useBookShipmentQuotePolling } from "./Hooks/useBookShipmentQuotePolling";
import { BookShipmentQuoteResults } from "./Internal/bookShipmentQuoteResults";
import { BookShipmentQuotesCopy } from "./Internal/bookShipmentQuotesCopy";
import { useBookShipmentWarningMessages } from "./Hooks/useBookShipmentWarningMessages";
import {
  loadFromShipmentToModify,
  resetShipment,
} from "@/Slices/bookShipmentSlice";
import { useAuthentication } from "@/Slices/authenticationSlice";
import { BookShipmentConfirmRestrictionsDialog } from "./Dialogs/BookShipmentConfirmRestrictionsDialog";
import type { QuoteRestriction } from "@freightsimple/generated-dashboard-openapi-client/models/QuoteRestriction";

interface BookShipmentQuotesScreenProps {
  shipment: PreBookingShipment;
  savedLocations: Array<SavedLocation>;
  defaultPickupLocationId: UUID | undefined;
  defaultDeliveryLocationId: UUID | undefined;
}

function BookShipmentQuotesScreen(props: BookShipmentQuotesScreenProps) {
  const {
    shipment,
    savedLocations,
    defaultPickupLocationId,
    defaultDeliveryLocationId,
  } = props;
  const shipmentsService = useShipmentService();

  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { isAuthenticated } = useAuthentication();

  const {
    shipmentId,
    pickupDate,
    pickupLocation,
    deliveryLocation,
    deliveryDeadline,
    lineItems,
    linearFeet,
    addInsuranceToShipment,
    insuranceAmount,
    insuranceCurrency,
    stateOfGoods,
    equipmentType,
    exclusiveUseNeeded,
    tarpRequired,
    manualQuotingOpen,
  } = shipment;

  const { warnings } = useBookShipmentWarningMessages({
    pickupDate,
    lineItems,
    pickupLocation,
    deliveryLocation,
  });

  const location = useLocation();

  useEffect(() => {
    function onPop() {
      if (location.pathname === "/book/details") {
        const shipmentIdToDelete = shipmentId;
        dispatch(
          loadFromShipmentToModify({
            shipment,
            savedLocations,
          }),
        );
        shipmentsService.deleteQuotedShipment(
          shipmentIdToDelete,
          "Modified for new quotes",
        );
        navigate("/book/details");
      }
    }

    window.addEventListener("popstate", onPop);

    return () => window.removeEventListener("popstate", onPop);
  }, []);

  // Set when the chosen quote carries restrictions the customer must
  // acknowledge before we proceed with the booking.
  const [restrictionsPrompt, setRestrictionsPrompt] = useState<
    { quoteId: UUID; restrictions: QuoteRestriction[] } | undefined
  >();

  // Which action is waiting on the keep-or-discard dialog. Replaces the old
  // imperative useMultiOptionConfirmDialog hooks — the dialog is declarative
  // and the pending action resolves via completeModify/completeRestart.
  const [keepQuotesPrompt, setKeepQuotesPrompt] = useState<
    "modify" | "restart" | undefined
  >();

  const { quotes, loadingQuotes, quotingDisabled } =
    useBookShipmentQuotePolling(shipmentId);

  async function proceedWithQuote(quoteId: UUID) {
    await shipmentsService.selectQuote(shipmentId, quoteId);
    navigate(`/book/pickup-address?shipmentId=${shipmentId}`);
  }

  async function onSelectQuote(quoteId: UUID) {
    const selectedQuote = quotes?.find((q) => q.quoteId === quoteId);
    const restrictions = selectedQuote?.restrictions ?? [];

    if (restrictions.length > 0) {
      setRestrictionsPrompt({ quoteId, restrictions });
      return;
    }

    await proceedWithQuote(quoteId);
  }

  // TODO : Move this up to the router level
  async function completeModify(keepQuotes: boolean) {
    const shipmentIdToDelete = shipmentId;

    dispatch(loadFromShipmentToModify({ shipment, savedLocations }));
    if (!keepQuotes) {
      await shipmentsService.deleteQuotedShipment(
        shipmentIdToDelete,
        "Modified for new quotes",
      );
    }

    navigate("/book/details");
  }

  function onModify() {
    // If there are no quotes, there is nothing to keep — treat as discard.
    if (quotes?.length === 0) {
      completeModify(false);
    } else {
      setKeepQuotesPrompt("modify");
    }
  }

  async function onRequestManualQuotes(
    shipmentId: UUID,
    equipmentType: EquipmentType,
    sharedDedicated: SharedDedicated,
    tarpRequired: boolean | undefined,
    linearFeet: number | undefined,
    notes: string,
  ) {
    await shipmentsService.requestManualQuotes(
      shipmentId,
      equipmentType,
      sharedDedicated,
      tarpRequired,
      linearFeet,
      notes,
    );
  }

  async function completeRestart(keepQuotes: boolean) {
    const shipmentIdToDelete = shipmentId;

    navigate("/book/details");
    dispatch(
      resetShipment({
        savedLocations,
        defaultPickupLocationId,
        defaultDeliveryLocationId,
      }),
    );
    if (!keepQuotes) {
      await shipmentsService.deleteQuotedShipment(
        shipmentIdToDelete,
        "Restarted, discarded quotes",
      );
    }
  }

  function onRestart() {
    setKeepQuotesPrompt("restart");
  }

  // Closing the dialog (X / backdrop) is a cancel: no navigation, keep state.
  function decideKeepQuotes(keepQuotes: boolean) {
    const pendingAction = keepQuotesPrompt;
    setKeepQuotesPrompt(undefined);
    if (pendingAction === "modify") {
      completeModify(keepQuotes);
    } else if (pendingAction === "restart") {
      completeRestart(keepQuotes);
    }
  }

  const loadedHeadline = manualQuotingOpen
    ? `🦸‍♀️ We are preparing more quotes`
    : "Quotes for your shipment";

  const headline = loadingQuotes ? "Fetching Quotes" : loadedHeadline;

  // Declared above the early returns below so the dialogs survive them: quote
  // polling can flip these guards while a dialog is open, and an early return
  // without the dialogs would silently unmount it (a screen that owns an open
  // Dialog must never unmount it).
  const quoteDialogs = (
    <>
      <BookShipmentConfirmRestrictionsDialog
        open={restrictionsPrompt !== undefined}
        onOpenChange={(open) => {
          if (!open) setRestrictionsPrompt(undefined);
        }}
        restrictions={restrictionsPrompt?.restrictions ?? []}
        onConfirmed={() => {
          const prompt = restrictionsPrompt;
          setRestrictionsPrompt(undefined);
          if (prompt) {
            proceedWithQuote(prompt.quoteId);
          }
        }}
      />
      <Dialog
        open={keepQuotesPrompt !== undefined}
        onOpenChange={(open) => {
          if (!open) {
            setKeepQuotesPrompt(undefined);
          }
        }}
        title={`Would you like to keep the current quotes before ${
          keepQuotesPrompt === "restart" ? "restarting" : "modifying"
        }?`}
        description="You will be able to find them in the 'Recent Quotes' tab."
        footer={
          <>
            <Button onClick={() => decideKeepQuotes(false)}>
              👎&nbsp; Discard
            </Button>
            <Button primary onClick={() => decideKeepQuotes(true)}>
              👍&nbsp; Keep
            </Button>
          </>
        }
      >
        <ControlPresentationProvider presentation="underlined">
          <></>
        </ControlPresentationProvider>
      </Dialog>
    </>
  );

  if (quotingDisabled) {
    return (
      <>
        {quoteDialogs}
        <BookShipmentQuotingDisabled />
      </>
    );
  }

  if (!loadingQuotes && quotes?.length === 0) {
    return (
      <>
        {quoteDialogs}
        <BookShipmentNoQuotes
          onModify={onModify}
          onRequestManualQuotes={onRequestManualQuotes}
          shipment={shipment}
        />
      </>
    );
  }

  if (!isAuthenticated) {
    return (
      <>
        {quoteDialogs}
        <ShipmentLoadingError code={401} />
      </>
    );
  }

  return (
    <Stack gap={8} align="center">
      <Stack gap={0} align="center">
        {/* Replica of the legacy Cargo Heading0 (32px bold). */}
        <Heading level={1}>{headline}</Heading>

        {!loadingQuotes && (
          <BookShipmentQuotesCopy
            onModify={onModify}
            onRestart={onRestart}
            manualQuotingOpen={manualQuotingOpen}
          />
        )}

        {loadingQuotes && (
          <T size="base" color="secondary">
            Connecting with carriers to find the best options for your
            shipment{" "}
          </T>
        )}
      </Stack>
      {warnings.length > 0 && (
        <T size="base" color="secondary">
          <BookShipmentWarningMessages warnings={warnings} />
        </T>
      )}
      {quoteDialogs}
      <BookShipmentQuoteResults
        waitingForQuotes={loadingQuotes}
        quotes={quotes}
        onModify={onModify}
        onBook={onSelectQuote}
        pickupDate={moment(pickupDate)}
        pickupLocation={pickupLocation}
        deliveryLocation={deliveryLocation}
        deliveryDeadline={deliveryDeadline}
        lineItems={lineItems}
        linearFeet={linearFeet}
        shipment={shipment}
        onRequestManualQuotes={onRequestManualQuotes}
        addInsuranceToShipment={addInsuranceToShipment}
        insuranceAmount={insuranceAmount}
        insuranceCurrency={insuranceCurrency}
        stateOfGoods={stateOfGoods}
        equipmentType={equipmentType}
        exclusiveUse={exclusiveUseNeeded}
        tarpRequired={tarpRequired}
      />
    </Stack>
  );
}

export default BookShipmentQuotesScreen;
